import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { Input, Button, Grid, Confirm } from 'semantic-ui-react';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { useSelector, useDispatch } from 'react-redux';

import {
  ModifierPropType,
  ModifierSetPropType,
} from 'components/modules/catalog/proptypes/catalog';
import { DismissibleMessage } from 'components/modules/utils/components';
import { multiCaseGet } from 'libs/format';
import { selectBusinessId, selectBusinessType } from 'selectors';
import { selectBusinessCatalogItems } from 'selectors/catalog';

import { getValidationMessage } from './EditMenuItemModifierSet.utils';
import {
  updateMenuModifierSet as updateMenuModifierSetConnect,
  deleteMenuModifierSet as deleteMenuModifierSetConnect,
  updateMenuModifier as updateMenuModifierConnect,
  addMenuModifier as addMenuModifierConnect,
  deleteMenuModifier as deleteMenuModifierConnect,
  flagMenuSaved as flagMenuSavedConnect,
} from '../../../../../actions/business';
import API from '../../../../../libs/api';
import HelpTooltip from '../../../../common/HelpTooltip';
import PriceField from '../../../../fields/PriceField';
import EditMenuModal from '../../common/EditMenuModal';
import PriceLabelToggle from '../../common/PriceLabelToggle/PriceLabelToggle';
import AddModifier from '../../modifier/AddModifier';
import DeleteModifier from '../../modifier/DeleteModifier';

const propTypes = {
  index: PropTypes.number.isRequired,
  modifierSet: ModifierSetPropType,
  modifiers: PropTypes.arrayOf(ModifierPropType),
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
};

const defaultProps = {
  modifierSet: {},
  modifiers: [],
  open: false,
  onOpen: () => {},
  onClose: () => {},
  onDelete: () => {},
};

export default function EditMenuItemModifierSet({
  index,
  modifierSet: initialModifierSet,
  itemId,
  itemIndex,
  modifiers: initialModifiers,
  open,
  onOpen,
  onClose,
  onCreate,
  onDelete,
}) {
  const dispatch = useDispatch();
  const businessId = useSelector(selectBusinessId);
  const businessType = useSelector(selectBusinessType);

  const items = useSelector((state) => selectBusinessCatalogItems(state, { itemIds: [itemId] }));
  const item = _get(items, '[0]', {});

  const { id, localId } = initialModifierSet || {};

  function getInitialState() {
    return {
      ..._cloneDeep(initialModifierSet || {}),
      modifiers: _cloneDeep(initialModifiers || []),
    };
  }

  const [formData, setFormData] = useState(getInitialState());

  const { modifiers, name, description, minAllowed, maxAllowed } = formData;

  const isNew = _isNil(id);

  const [errorMessage, setErrorMessage] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const initialIsMoney = _isEmpty(_get(modifiers, '[0].priceDisplay', ''));
  const [isMoney, setIsMoney] = useState(initialIsMoney);

  function onChange(e, { name: property, value: newValue }) {
    setFormData({ ...formData, [property]: newValue });
  }

  function onModifierAdd() {
    setFormData((previousFormData) => {
      const { modifiers: previousModifiers } = previousFormData;
      const newModifiers = [...previousModifiers];
      newModifiers.push({});
      return { ...previousFormData, modifiers: newModifiers };
    });

    dispatch(addMenuModifierConnect({ modifierSetIndex: index }));
  }

  function onModifierChange(e, { name: property, value: newValue, modifierLocalIndex }) {
    setFormData((previousFormData) => {
      const { modifiers: previousModifiers } = previousFormData;
      const newModifiers = [...previousModifiers];
      newModifiers[modifierLocalIndex] = {
        ...newModifiers[modifierLocalIndex],
        [property]: newValue,
      };
      return { ...previousFormData, modifiers: newModifiers };
    });
  }

  function onPriceToggle(e, { checked }) {
    modifiers.forEach(({ localIndex: modifierLocalIndex }) => {
      if (!checked) {
        onModifierChange(e, { name: 'priceDisplay', value: '', modifierLocalIndex });
      }
    });
    setIsMoney(!checked);
  }

  function onModifierRemove(e, { modifierLocalIndex }) {
    setFormData((previousFormData) => {
      const { modifiers: previousModifiers } = previousFormData;
      const newModifiers = [...previousModifiers];
      newModifiers.splice(modifierLocalIndex, 1);
      return { ...previousFormData, modifiers: newModifiers };
    });
  }

  useEffect(() => {
    const newModifiers = initialModifiers.map((initialModifier, modifierLocalIndex) => {
      const { index: modifierIndex, id: modifierId, localId: modifierLocalId } = initialModifier;

      return {
        ..._cloneDeep(modifiers[modifierLocalIndex]),
        index: modifierIndex,
        localIndex: modifierLocalIndex,
        id: modifierId,
        localId: modifierLocalId,
      };
    });

    setFormData((previousFormData) => {
      return { ...previousFormData, modifiers: newModifiers };
    });
  }, [_get(initialModifiers, 'length', 0)]);

  async function onSaveNewModifier(modifier) {
    try {
      const { index: modifierIndex, ...rest } = modifier;
      const { data: newModifierSet } = await API.createMenuModifier(businessId, businessType, rest);

      dispatch(
        updateMenuModifierConnect({
          index: modifierIndex,
          values: newModifierSet,
          clearDirty: true,
        }),
      );

      return newModifierSet.id;
    } catch (e) {
      setErrorMessage('There was an error saving modifier');
      return null;
    }
  }

  async function onSaveNewModifierSet() {
    const validationMessage = getValidationMessage(formData);
    if (!_isEmpty(validationMessage)) {
      setErrorMessage(validationMessage);
      return;
    }

    try {
      setSaveLoading(true);

      const newModifiers = (modifiers || []).filter(({ id: modifierId }) => !modifierId);
      let newModifierIds = await Promise.all(
        newModifiers.map((newModifier) => onSaveNewModifier(newModifier)),
      );
      newModifierIds = newModifierIds.filter((val) => val);

      const { data: newModifierSet } = await API.createMenuModifierSet(businessId, businessType, {
        name,
        description,
        minAllowed,
        maxAllowed,
        modifiers: newModifierIds,
      });

      const newItemModifierSets = [
        ...new Set([
          ...item.modifierSets.filter((modifierSetId) => typeof modifierSetId !== 'string'),
          newModifierSet.id,
        ]),
      ];

      const { data: updatedItem } = await API.addMenuItemModifierSet(
        businessId,
        businessType,
        itemId,
        newItemModifierSets,
      );

      dispatch(
        updateMenuModifierSetConnect({
          index,
          values: newModifierSet,
          itemIndex,
          localModifierSetId: localId,
          modifierSets: multiCaseGet(updatedItem, 'modifierSets'),
        }),
      );

      await onCreate({ id: newModifierSet.id });

      onClose();
      dispatch(flagMenuSavedConnect(true));
    } catch (e) {
      setErrorMessage('There was an error saving modifier set');
    } finally {
      setSaveLoading(false);
    }
  }

  async function onSaveModifier(modifier) {
    try {
      const { index: modifierIndex, id: modifierId, ...rest } = modifier;
      const { data: updatedModifier } = await API.updateMenuModifier(
        businessId,
        businessType,
        modifierId,
        rest,
      );

      dispatch(
        updateMenuModifierConnect({
          index: modifierIndex,
          values: updatedModifier,
          clearDirty: true,
        }),
      );
    } catch (e) {
      setErrorMessage('There was an error saving modifier');
    }
  }

  async function onSaveModifierSet() {
    const validationMessage = getValidationMessage(formData);
    if (!_isEmpty(validationMessage)) {
      setErrorMessage(validationMessage);
      return;
    }

    try {
      setSaveLoading(true);

      const newModifiers = (modifiers || []).filter(({ id: modifierId }) => !modifierId);
      const existingModifiers = (modifiers || []).filter(({ id: modifierId }) => modifierId);

      let newModifierIds = await Promise.all(
        newModifiers.map((newModifier) => onSaveNewModifier(newModifier)),
      );
      newModifierIds = newModifierIds.filter((val) => val);

      const existingModifiersIds = existingModifiers.map(({ id: modifierId }) => modifierId);
      await Promise.all(
        existingModifiers.map((existingModifier) => onSaveModifier(existingModifier)),
      );

      const { data: updatedModifierSet } = await API.updateMenuModifierSet(
        businessId,
        businessType,
        id,
        {
          name,
          description,
          minAllowed,
          maxAllowed,
          modifiers: [...existingModifiersIds, ...newModifierIds],
        },
      );

      dispatch(
        updateMenuModifierSetConnect({
          index,
          values: updatedModifierSet,
        }),
      );

      onClose();

      dispatch(flagMenuSavedConnect(true));
    } catch (e) {
      setErrorMessage('There was an error saving modifier set');
      setSaveLoading(false);
    } finally {
      setSaveLoading(false);
    }
  }

  function onDeleteNewModifier(modifierIndex, modifierLocalId, modifierLocalIndex) {
    onModifierRemove({}, { modifierLocalIndex });

    dispatch(
      deleteMenuModifierConnect({
        entityIndex: modifierIndex,
        entityId: modifierLocalId,
        entityRelationIndex: index,
      }),
    );
  }

  function onDeleteNewModifierSet(e) {
    try {
      dispatch(
        deleteMenuModifierSetConnect({
          index,
          itemIndex,
          modifierSetId: localId,
        }),
      );

      modifiers.forEach(
        ({ index: modifierIndex, localId: modifierLocalId, localIndex: modifierLocalIndex }) => {
          onDeleteNewModifier(modifierIndex, modifierLocalId, modifierLocalIndex);
        },
      );

      onDelete(e);
    } catch (error) {
      setErrorMessage('There was an error deleting the modifier set');
    }
  }

  async function onDeleteModifier(modifierIndex, modifierId, modifierLocalIndex) {
    try {
      await API.deleteMenuModifier(businessId, businessType, modifierId);

      onModifierRemove({}, { modifierLocalIndex });
      dispatch(
        deleteMenuModifierConnect({
          entityIndex: modifierIndex,
          entityId: modifierId,
          entityRelationIndex: index,
        }),
      );
    } catch (e) {
      setErrorMessage('There was an error deleting the modifier');
    }
  }

  async function onDeleteModifierSet(e) {
    try {
      setDeleteLoading(true);
      await API.deleteMenuModifierSet(businessId, businessType, id);

      dispatch(
        deleteMenuModifierSetConnect({
          index,
          itemIndex,
          modifierSetId: id,
        }),
      );

      await Promise.all(
        modifiers.map(({ index: modifierIndex, id: modifierId, localIndex: modifierLocalIndex }) =>
          onDeleteModifier(modifierIndex, modifierId, modifierLocalIndex),
        ),
      );

      onDelete(e);

      dispatch(flagMenuSavedConnect(true));
    } catch (error) {
      setErrorMessage('There was an error deleting the modifier set');
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <EditMenuModal
      open={open}
      openModal={onOpen}
      closeModal={(e) => {
        if (isNew) {
          onDeleteNewModifierSet(e);
        }
        onClose(e);
      }}
      className="edit-menu-schedule edit-menu-modifier-set"
      newItemTitle="Create Modifier Set"
      existingItemTitle="Edit Modifier Set"
      description='Modifiers are how you modify an item, like "Sides" or "Sauces" and can have different prices'
      isNewItem={isNew}
      content={
        <>
          <h2>
            <span className="red asterisk">Modifier Set Name</span>
            <HelpTooltip
              title="Modifier Set Name"
              content="Enter the name that the Customers should see when selecting the contained Modifiers. Examples are Side Choice, Dressings, Toppings..."
            />
          </h2>
          <Input
            defaultValue={name || ''}
            placeholder="e.g. Side Options"
            name="name"
            onChange={onChange}
          />
          <div className="menu-modifier-set-modifiers">
            <Grid>
              <Grid.Row>
                <Grid.Column width={10}>
                  <h2>
                    Modifiers
                    <HelpTooltip
                      title="Modifiers"
                      content="Create your Modifiers contained within this Modifier Set. You may optionally set prices for any or all of the Modifiers if they have an additional price. Not that any changes (addition, removal, pricing) are reflected anywhere this Modifier Set is used."
                    />
                  </h2>
                </Grid.Column>
                <Grid.Column width={6} textAlign="right">
                  <h2>Prices</h2>
                  <PriceLabelToggle defaultIsMoney={isMoney} onChange={onPriceToggle} />
                </Grid.Column>
              </Grid.Row>
              {modifiers.map(
                ({
                  name: modifierName,
                  price,
                  priceDisplay,
                  index: modifierIndex,
                  id: modifierId,
                  localId: modifierLocalId,
                  localIndex: modifierLocalIndex,
                }) => (
                  <Grid.Row key={`modifier_${modifierId || modifierLocalId}`}>
                    <Grid.Column width={12}>
                      <DeleteModifier
                        onClick={
                          _isNil(modifierId)
                            ? () =>
                                onDeleteNewModifier(
                                  modifierIndex,
                                  modifierLocalId,
                                  modifierLocalIndex,
                                )
                            : () => onDeleteModifier(modifierIndex, modifierId, modifierLocalIndex)
                        }
                      />
                      <Input
                        className="red asterisk after-big"
                        defaultValue={modifierName}
                        placeholder="Modifier Name"
                        onChange={(e, { value }) =>
                          onModifierChange(e, { name: 'name', value, modifierLocalIndex })
                        }
                      />
                    </Grid.Column>
                    <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                      <PriceField
                        priceInitialValue={price}
                        labelInitialValue={priceDisplay}
                        placeholder="0.00"
                        isMoney={isMoney}
                        onChange={(e, { value }) =>
                          onModifierChange(e, {
                            name: isMoney ? 'price' : 'priceDisplay',
                            value,
                            modifierLocalIndex,
                          })
                        }
                      />
                    </Grid.Column>
                  </Grid.Row>
                ),
              )}
            </Grid>
            <AddModifier onClick={onModifierAdd} />
          </div>
          <div style={{ color: 'red', marginTop: '1rem', paddingRight: '50%' }}>
            *Any edits to modifiers sets will be reflected across all items that have this
            associated modifier set.
          </div>
        </>
      }
      actions={
        <>
          {!_isEmpty(errorMessage) && (
            <DismissibleMessage
              onDismiss={() => setErrorMessage('')}
              delay={8}
              initialVisible
              error
              content={errorMessage}
            />
          )}
          {!isNew && (
            <>
              <Button
                className=""
                onClick={() => setDeleteConfirmationOpen(true)}
                loading={deleteLoading}
                disabled={deleteLoading}
                color="red"
                content="Delete"
              />
              <Confirm
                dimmer="inverted"
                className="delete-confirmation"
                content="Are you sure you want to delete this modifier set?"
                confirmButton={{
                  content: 'Delete',
                  loading: deleteLoading,
                  disabled: deleteLoading,
                }}
                open={deleteConfirmationOpen}
                onCancel={() => {
                  setDeleteConfirmationOpen(false);
                }}
                onConfirm={onDeleteModifierSet}
              />
            </>
          )}
          <Button
            className="action"
            onClick={isNew ? onSaveNewModifierSet : onSaveModifierSet}
            loading={saveLoading}
            disabled={saveLoading}
            content="Save"
          />
        </>
      }
    />
  );
}

EditMenuItemModifierSet.propTypes = propTypes;
EditMenuItemModifierSet.defaultProps = defaultProps;
